// Angular imports
import { Component, OnInit, Input, ViewChild, Output, EventEmitter, SimpleChanges, OnChanges } from '@angular/core';

// 3rd party imports
import { DxDropDownBoxComponent, DxDataGridComponent, DxTextBoxComponent } from 'devextreme-angular';

// CC-Common imports
import { DxWidgetUtils, IconConstants, Utils } from 'cc-common';

// Service imports
import { HelperService } from '../../../../_services/shared-services/helper.service';

// constant imports
import { ActionConstants } from '../../../../_constants/action-constants';

// Model Imports
import { CommunitySelectorResultDto } from '../../../../_models/community-selector-result-dto';

@Component({
    selector: 'app-multi-column-selector',
    templateUrl: './multi-column-selector.component.html',
    styleUrls: ['./multi-column-selector.component.scss']
})
export class MultiColumnSelectorComponent implements OnInit, OnChanges {
    @ViewChild('dropDownBox', { static: false }) dropdownBox: DxDropDownBoxComponent;
    @ViewChild('dropDownBoxTextBox', { static: false }) dropDownBoxTextBox: DxTextBoxComponent;
    @ViewChild('dataGrid', { static: false }) dataGrid: DxDataGridComponent;

    @Input() isRequired = false;
    @Input() validationGroup: string;
    @Input() validationCallback: (opt: any) => boolean;
    @Input() placeholder = 'Select a value...';
    @Input() gridSource: any;
    @Input() displayExpr: any;
    @Input() valueExpr: any;
    @Input() boxValue: any[];
    @Input() columnDef: any[];
    @Input() name: string;
    @Input() disabled = false;
    @Input() readOnly = false;
    @Input() dropDownButtonTemplate: any;
    @Input() dropDownOptions: object;
    @Input() isItemSelectionMandatory = false;
    @Input() showClearButton = true;
    @Input() cssClass = 'drop-down-box';
    @Input() wordWrapEnabled = false;
    @Input() showGridHeaders = true;
    @Input() avoidDefaultSort = false;
    @Input() navigateToCommunity = false;
    @Input() dropDownInputAttr: object = {};
    // NOTE:- As per Sandeep's suggetion related to ANG-2563 we no longer need to show Column Header Filter so removed binding showHeaderFilters from <dxo-header-filter>. we can use showHeaderFilters again if required in future.
    @Input() showHeaderFilters = false;

    @Output() editTemplateEvent = new EventEmitter<any>();
    @Output() gridSelectionEvent = new EventEmitter<any>();
    @Output() refreshSourceEvent = new EventEmitter<any>();
    @Output() detailTemplateEvent = new EventEmitter<any>();
    @Output() addTemplateEvent = new EventEmitter<any>();
    @Output() viewTemplateEvent = new EventEmitter<any>();
    @Output() amountClick = new EventEmitter<any>();

    searchText: string;
    textInputVal: string;
    focusedRowIndex = -1;
    isEditEnabled = false;
    selectedItem: any = null;
    inlineButtonTemplate: any[] = [];
    dropdownButtonList: any[] = [];
    buttonTemplate: string;
    elementAttr: object = {};

    iconProduction = IconConstants.PRODUCTION;
    iconTransition = IconConstants.IN_TRANSITION;
    iconTerminate = IconConstants.TERMINATE;
    iconCheck = IconConstants.CHECK;
    iconCross = IconConstants.CROSS;
    iconExclamatory = IconConstants.EXCLAMATION_CIRCLE;

    constructor(private readonly helperService: HelperService) {
        this.searchText = '';
        this.textInputVal = null;
        this.selectedItem = null;
        this.customValidationCallback = this.customValidationCallback.bind(this);
        this.onNavigateToCommunity = this.onNavigateToCommunity.bind(this);
        this.onClearSelection = this.onClearSelection.bind(this);
    }

    ngOnInit() {
        this.textInputVal = null;
        this.selectedItem = null;
        this.elementAttr = { class: this.cssClass };
        this.setSelectedRowText();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.gridSource && changes.gridSource.currentValue) {
            if (Utils.isFalse(this.avoidDefaultSort)) {
                this.gridSource.sort((ele1: any, ele2: any) => {
                    const name1 = this.toDisplayText(ele1).toLowerCase();
                    const name2 = this.toDisplayText(ele2).toLowerCase();
                    if (name1 > name2) {
                        return 1;
                    } else if (name1 < name2) {
                        return -1;
                    }
                    return 0;
                });
            }

            this.isElementSelected();
        }

        if (changes.boxValue && changes.boxValue.currentValue) {
            this.setSelectedRowText();
        }

        if (changes.columnDef && changes.columnDef.currentValue) {
            this.configureColumnsSearchEditor();
        }

        if (changes.dropDownButtonTemplate && changes.dropDownButtonTemplate.currentValue) {
            this.setDropdownButtonList();
            this.getDropdownButtonTemplate();
        }
    }

    customValidationCallback(opt: any): boolean {
        let isValid = Utils.isFalse(this.isRequired) || Utils.isTrue(this.readOnly);
        if (
            Utils.isFalse(isValid) &&
            (Utils.notNullOrEmpty(this.searchText) || Utils.notNullAndDefined(this.selectedItem))
        ) {
            isValid = Utils.notNullAndDefined(opt.value);
            if (Utils.notNullAndDefined(this.validationCallback)) {
                isValid = this.validationCallback(opt);
            }
        }
        return isValid;
    }

    onNavigateToCommunity(): void {
        if (Utils.notNullAndDefined(this.selectedItem)) {
            this.helperService.openDefaultCommunityPageInNewWindow(this.toItemValue(this.selectedItem));
        }
    }

    onClearSelection(): void {
        this.clearSelection();
        setTimeout(() => {
            if (DxWidgetUtils.hasInstance(this.dropdownBox)) {
                this.dropdownBox.instance.open();
                this.dropDownBoxTextBox.instance.focus();
            }
        }, 200);
    }

    onDropdownOpen(): void {
        setTimeout(() => {
            if (Utils.notNullAndDefined(this.selectedItem) && DxWidgetUtils.hasInstance(this.dataGrid)) {
                this.searchText = '';
                this.dataGrid.instance.navigateToRow(this.toItemValue(this.selectedItem));
            }
        }, 100);
    }

    onDropdownClose(): void {
        if (Utils.notNullAndDefined(this.selectedItem)) {
            this.textInputVal = this.toDisplayText(this.selectedItem);
            this.boxValue = [this.toItemValue(this.selectedItem)];
        }
    }

    onAmountClick(amount: string): void {
        this.amountClick.emit(amount);
    }

    onTextFocusIn(event: any): void {
        if (Utils.isFalse(this.readOnly)) {
            const isOpened = this.dropdownBox.instance.option('opened');
            if (Utils.isFalse(isOpened)) {
                this.dropdownBox.instance.open();
                if (Utils.equalsIgnoreCase(typeof event.event.target.select, 'function')) {
                    setTimeout(() => event.event.target.select(), 100);
                }
            }
        }
    }

    onTextFocusOut(event: any): void {
        const isOpened = this.dropdownBox.instance.option('opened');
        if (Utils.isFalse(isOpened)) {
            this.dropdownBox.instance.close();
            if (Utils.equalsIgnoreCase(typeof event.event.target.blur, 'function')) {
                setTimeout(() => event.event.target.blur(), 100);
            }
        }
    }

    onTextKeyUp(event: any): void {
        if (Utils.isFalse(this.readOnly) && Utils.notNullAndDefined(event) && Utils.notNullAndDefined(event.event)) {
            if (Utils.equalsIgnoreCase(event.event.key, 'enter')) {
                if (this.focusedRowIndex >= 0) {
                    this.dataGrid.instance.selectRowsByIndexes([this.focusedRowIndex]);
                } else if (Utils.isNullOrEmpty(this.searchText)) {
                    this.clearSelection();
                }
            } else if (Utils.notEqualsIgnoreCase(event.event.target.value.trim(), this.searchText)) {
                this.searchText = event.event.target.value.trim();
                this.focusedRowIndex = -1;
            }
        }
    }

    getStatus(rowData: CommunitySelectorResultDto, status: string): string {
        const date = this.helperService.getDateString(rowData.terminatingDate);
        return Utils.isTrue(rowData.isTerminating) && Utils.notEqualsIgnoreCase(rowData.status, 'Terminated')
            ? `Community will terminate on ${date}`
            : status;
    }

    onTextKeyDown(event: any): void {
        if (Utils.isFalse(this.readOnly) && Utils.notNullAndDefined(event) && Utils.notNullAndDefined(event.event)) {
            if (Utils.equalsIgnoreCase(event.event.key, 'escape')) {
                this.onTextFocusOut(event);
            } else {
                this.onTextFocusIn(event);
            }
            if (Utils.equalsIgnoreCase(event.event.key, 'arrowdown')) {
                this.focusedRowIndex += 1;
            } else if (Utils.equalsIgnoreCase(event.event.key, 'arrowup') && this.focusedRowIndex > 0) {
                this.focusedRowIndex -= 1;
            }
        }
    }

    onValueChanged(event: any): void {
        if (Utils.isNullOrEmpty(event.value) && Utils.notNullAndDefined(this.selectedItem)) {
            this.clearSelection();
        }
    }

    onSelectionChanged(event: any): void {
        if (
            Utils.isNullOrUndefined(this.boxValue) ||
            (Utils.notNullOrEmpty(event.selectedRowKeys) &&
                Utils.notEqualsIgnoreCase(this.boxValue.join(), event.selectedRowKeys.join()))
        ) {
            this.boxValue = event.selectedRowKeys;
            this.raiseGridSelectionEvent();
        }
    }

    onRowClick(event: any): void {
        this.dropdownBox.instance.close();
        if (Utils.isTrue(event.isSelected) && Utils.isNullOrEmpty(this.boxValue)) {
            this.boxValue = event.component.getSelectedRowKeys();
            this.raiseGridSelectionEvent();
        }
    }

    onTemplateButtonClick(event: any, button: any): void {
        const pointerEvent = event.event;
        pointerEvent.stopPropagation();
        if (Utils.equalsIgnoreCase(button.name, ActionConstants.CLEAR)) {
            this.clearSelection();
        } else if (Utils.equalsIgnoreCase(button.name, ActionConstants.EDIT)) {
            this.editTemplateEvent.emit();
        } else if (Utils.equalsIgnoreCase(button.name, ActionConstants.REFRESH)) {
            this.refreshSourceEvent.emit();
        } else if (Utils.equalsIgnoreCase(button.name, ActionConstants.DETAIL)) {
            this.detailTemplateEvent.emit();
        } else if (Utils.equalsIgnoreCase(button.name, ActionConstants.ADD)) {
            this.addTemplateEvent.emit();
        } else if (Utils.equalsIgnoreCase(button.name, ActionConstants.VIEW)) {
            this.viewTemplateEvent.emit(this.selectedItem);
        }
    }

    private clearSelection(): void {
        this.resetSelector();
        this.gridSelectionEvent.emit(false);
    }

    private isElementSelected(): void {
        if (Utils.isTrue(this.isItemSelectionMandatory)) {
            setTimeout(() => {
                if (Utils.notNullOrEmpty(this.gridSource) && Utils.notNullOrEmpty(this.boxValue)) {
                    this.isEditEnabled = Utils.notNullAndDefined(this.selectedItem);
                } else {
                    this.isEditEnabled = false;
                }
            }, 100);
        } else {
            setTimeout(() => (this.isEditEnabled = true), 100);
        }
    }

    private setSelectedRowText(notify = true): void {
        if (Utils.notNullOrEmpty(this.gridSource) && Utils.notNullOrEmpty(this.boxValue)) {
            const selectedRows = this.gridSource.filter((x: any) =>
                Utils.equals(this.toItemValue(x), this.boxValue[0])
            );
            if (Utils.notNullOrEmpty(selectedRows)) {
                this.selectedItem = selectedRows[0];
                this.textInputVal = this.toDisplayText(this.selectedItem);
                this.searchText = this.textInputVal;
                this.isElementSelected();
                const selectedRowIndex = this.gridSource.indexOf(this.selectedItem);
                const selectedObject = { selectedRow: selectedRows, selectedRowIndex };
                if (notify) {
                    this.gridSelectionEvent.emit(selectedObject);
                }
            }
        } else {
            this.selectedItem = null;
            this.textInputVal = null;
            this.searchText = '';
            this.focusedRowIndex = -1;
            if (DxWidgetUtils.hasInstance(this.dataGrid)) {
                this.dataGrid.instance.clearFilter();
                this.dataGrid.instance.clearSelection();
                this.dataGrid.instance.option('focusedRowIndex', -1);
                this.dataGrid.instance.option('focusedRowKey', null);
                this.dataGrid.instance.repaint();
            }
        }
    }

    private getDropdownButtonTemplate(): void {
        this.inlineButtonTemplate = [];
        this.buttonTemplate = 'dropDownArrayButton';
        if (Utils.equalsIgnoreCase(this.dropDownButtonTemplate, 'dropDownEditButton')) {
            this.isElementSelected();
            this.inlineButtonTemplate = this.dropdownButtonList.filter(
                (d) =>
                    Utils.equalsIgnoreCase(d.name, ActionConstants.EDIT) ||
                    Utils.equalsIgnoreCase(d.name, ActionConstants.CLEAR)
            );
        } else if (
            Utils.notEqualsIgnoreCase(typeof this.dropDownButtonTemplate, 'string') &&
            Utils.notNullOrEmpty(this.dropDownButtonTemplate)
        ) {
            this.dropDownButtonTemplate.forEach((element) => {
                if (Utils.equalsIgnoreCase(element, 'dropDownEditButton')) {
                    this.isElementSelected();
                    this.inlineButtonTemplate.push(
                        this.dropdownButtonList.find((d) => Utils.equalsIgnoreCase(d.name, ActionConstants.EDIT))
                    );
                }
                if (Utils.equalsIgnoreCase(element, 'dropDownRefreshButton')) {
                    this.inlineButtonTemplate.push(
                        this.dropdownButtonList.find((d) => Utils.equalsIgnoreCase(d.name, ActionConstants.REFRESH))
                    );
                }
                if (Utils.equalsIgnoreCase(element, 'dropDownDetailButton')) {
                    this.isElementSelected();
                    this.inlineButtonTemplate.push(
                        this.dropdownButtonList.find((d) => Utils.equalsIgnoreCase(d.name, ActionConstants.DETAIL))
                    );
                }
                if (Utils.equalsIgnoreCase(element, 'dropDownAddButton')) {
                    this.inlineButtonTemplate.push(
                        this.dropdownButtonList.find((d) => Utils.equalsIgnoreCase(d.name, ActionConstants.ADD))
                    );
                    setTimeout(() => (this.isEditEnabled = true), 200);
                }
                if (Utils.equalsIgnoreCase(element, 'dropDownViewButton')) {
                    this.isElementSelected();
                    this.inlineButtonTemplate.push(
                        this.dropdownButtonList.find((d) => Utils.equalsIgnoreCase(d.name, ActionConstants.VIEW))
                    );
                }
                if (
                    Utils.equalsIgnoreCase(element, 'dropDownClearButton') &&
                    Utils.notNullAndDefined(this.selectedItem)
                ) {
                    this.isElementSelected();
                    this.inlineButtonTemplate.push(
                        this.dropdownButtonList.find((d) => Utils.equalsIgnoreCase(d.name, ActionConstants.CLEAR))
                    );
                }
            });
        } else {
            this.inlineButtonTemplate = [];
            this.buttonTemplate = 'dropDownButton';
        }
    }

    private setDropdownButtonList(): void {
        this.dropdownButtonList = [
            {
                name: ActionConstants.ADD,
                text: 'Add',
                icon: IconConstants.ADD,
                class: 'select-default-button',
                type: 'default'
            },
            {
                name: ActionConstants.EDIT,
                text: this.name + ' Edit',
                icon: IconConstants.EDIT_NEW,
                class: 'select-default-button',
                type: 'default'
            },
            {
                name: ActionConstants.REFRESH,
                text: null,
                type: 'normal',
                icon: IconConstants.REFRESH,
                class: 'select-normal-button'
            },
            {
                name: ActionConstants.VIEW,
                text: null,
                hint: 'View',
                type: 'default',
                icon: IconConstants.VIEW,
                class: 'select-normal-button',
                stylingMode: 'text'
            },
            {
                name: ActionConstants.CLEAR,
                text: null,
                hint: 'Clear Selection',
                type: 'default',
                icon: IconConstants.CLEAR,
                stylingMode: 'text',
                class: 'select-normal-button'
            },
            {
                name: ActionConstants.DETAIL,
                text: 'Details',
                icon: IconConstants.INFO,
                class: 'select-default-button',
                type: 'default'
            }
        ];
    }

    private configureColumnsSearchEditor(): void {
        this.columnDef.forEach((col) => {
            if (Utils.notNullAndDefined(col.editorOptions)) {
                col.editorOptions.placeholder = '';
            }
        });
    }

    private raiseGridSelectionEvent(): void {
        const selectedRow = this.dataGrid.instance.getSelectedRowsData();
        const selectedRowIndex = this.dataGrid.instance.getRowIndexByKey(this.boxValue[0]);

        if (selectedRow.length > 0) {
            this.selectedItem = selectedRow[0];
            this.textInputVal = this.toDisplayText(this.selectedItem);
            this.searchText = this.textInputVal;
        } else {
            this.textInputVal = null;
            this.selectedItem = null;
            this.searchText = '';
        }
        this.isElementSelected();
        const selectedObject = { selectedRow, selectedRowIndex };
        if (Utils.notNullOrEmpty(selectedRow)) {
            this.gridSelectionEvent.emit(selectedObject);
        } else {
            this.gridSelectionEvent.emit(false);
        }

        setTimeout(() => {
            this.dropdownBox.instance.close();
            this.dropDownBoxTextBox.instance.blur();
        }, 100);
    }

    private toDisplayText(item: any): string {
        if (Utils.notEqualsIgnoreCase(typeof this.displayExpr, 'string')) {
            return this.displayExpr(item);
        }
        return Utils.notNullAndDefined(item[this.displayExpr]) ? item[this.displayExpr] : '';
    }

    private toItemValue(item: any): any {
        if (Utils.notEqualsIgnoreCase(typeof this.valueExpr, 'string')) {
            return this.valueExpr(item);
        }
        return item[this.valueExpr];
    }

    public resetSelector(): void {
        this.searchText = '';
        this.boxValue = null;
        this.textInputVal = null;
        this.focusedRowIndex = -1;
        this.selectedItem = null;
        this.isEditEnabled = false;
        if (DxWidgetUtils.hasInstance(this.dataGrid)) {
            this.dataGrid.instance.clearFilter();
            this.dataGrid.instance.clearSelection();
            this.dataGrid.instance.option('focusedRowIndex', -1);
        }

        this.isElementSelected();
    }

    public revertSelection(boxValue: any[]): void {
        this.boxValue = boxValue;
        this.setSelectedRowText(false);
    }

    public setSelection(boxValue: any[]): void {
        this.boxValue = boxValue;
        this.setSelectedRowText(true);
    }

    public repaint(): void {
        this.dropdownBox.instance.repaint();
    }
}
